import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { EllipsisText, formatDateTime, JsonBlock } from '../Assessments/shared';
import { LlmCallDetail, LlmCallRow } from './actionTypes';
import { fetchLlmCall, fetchLlmCalls, LlmCallFilters } from './actionsApi';
import ScenarioErrorBoundary from './ErrorBoundary';
import { readableError } from './errors';
import { describeActor } from './Jobs';

/**
 * What the model was asked and what it answered, for a draft, a headline or a
 * job.
 *
 * `pm.llm` is installed unconditionally, so this trail exists in production
 * today and is surfaced nowhere. Nothing here is redacted and nothing needs
 * to be: system prompts are product copy, user prompts are headlines already
 * shipped to trainees, and a provider's key never enters the messages (it
 * goes in a header). The acting user IS shown — that is the point.
 *
 * Two honesty rules the copy enforces. Cost renders as "unmeasured", never as
 * a zero, because LLMProvider carries no price column. And where a call has
 * no job id (every row written before the job ledger existed) the header says
 * "calls about this draft" and gives the window — never "this mint's calls".
 */

const PAGE_SIZE = 25;

const STATUS_COLOR: Record<string, string> = {
  ok: 'green',
  success: 'green',
  error: 'red',
  failed: 'red',
  timeout: 'orange',
};

/** The call_type prefix every scenario-builder call shares carries no
 *  information in a scenario-builder screen. */
const displayCallType = (value: string) =>
  String(value || '').replace(/^scenario_builder_/, '');

const chips = (row: LlmCallRow) => (
  <Space size={[4, 4]} wrap className="assessment-tag-group">
    {row.salvaged && (
      <Tag color="gold" title="The answer was repaired before it parsed.">
        SALVAGED
      </Tag>
    )}
    {row.thinking_leaked && (
      <Tag
        color="purple"
        title="Reasoning tokens were billed while thinking was suppressed."
      >
        THINKING LEAKED
      </Tag>
    )}
    {row.attempts && row.attempts.length > 1 && (
      <Tag color="blue">FAILOVER {row.attempts.length}</Tag>
    )}
  </Space>
);

const CallDetail: React.FC<{ call: LlmCallDetail }> = ({ call }) => {
  const messages = call.messages;
  return (
    <div className="assessment-detail-stack">
      <Descriptions bordered size="small" column={3}>
        <Descriptions.Item label="Call type">
          <EllipsisText code value={displayCallType(call.call_type)} />
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag
            color={STATUS_COLOR[String(call.status).toLowerCase()] || 'default'}
          >
            {String(call.status).toUpperCase()}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Answered by">
          <EllipsisText code value={call.model} />
        </Descriptions.Item>
        <Descriptions.Item label="Provider">
          <EllipsisText value={call.provider} />
        </Descriptions.Item>
        <Descriptions.Item label="Latency">
          {call.latency_ms != null ? `${call.latency_ms} ms` : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Cost">
          <Typography.Text type="secondary">unmeasured</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Prompt tokens">
          {call.tokens_prompt ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Completion tokens">
          {call.tokens_completion ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Reasoning tokens">
          {call.tokens_reasoning ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Requested by">
          {/* The serializer sends {id, username}. Rendering it straight into
              JSX threw "Objects are not valid as a React child" and took the
              whole admin down with it; `describeActor` is the module's one
              way of turning any actor shape into a name. */}
          {describeActor(call.user)}
        </Descriptions.Item>
        <Descriptions.Item label="Job">
          <EllipsisText code value={call.job_id} />
        </Descriptions.Item>
        <Descriptions.Item label="Subject">
          {call.ref_type ? `${call.ref_type} #${call.ref_id ?? '?'}` : '—'}
        </Descriptions.Item>
      </Descriptions>

      {call.truncated && (
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="This transcript is clipped for transport"
          description={`The stored prompt and answer are longer than the ${
            call.truncated_at ?? 16000
          } characters shown here.`}
        />
      )}

      <Card size="small" title="What the model was asked">
        {Array.isArray(messages) ? (
          messages.map((message, index) => (
            <div key={index}>
              <Typography.Text strong>{message.role}</Typography.Text>
              <pre className="assessment-json">{message.content}</pre>
            </div>
          ))
        ) : (
          <pre className="assessment-json">{String(messages || '—')}</pre>
        )}
      </Card>

      <Card size="small" title="What the model answered">
        <pre className="assessment-json">{call.output || '—'}</pre>
      </Card>

      {call.attempts && call.attempts.length > 0 && (
        <Card size="small" title={`Failover chain (${call.attempts.length})`}>
          <JsonBlock compact value={call.attempts} />
        </Card>
      )}

      {call.metadata && (
        <Card size="small" title="Metadata">
          <JsonBlock compact value={call.metadata} />
        </Card>
      )}
    </div>
  );
};

type Props = {
  /** The subject: a draft (`ref_type`/`ref_id`) or a job (`job`). */
  filters: LlmCallFilters;
  title?: string;
  /**
   * One draft is the subject of two different kinds of call — the retelling
   * (`scenario_rendition`) and the briefing/macro work (`scenario_draft`) —
   * and a bare `ref_id` across kinds would fold in another model's row #49.
   * When the parent knows the kinds, the screen picks between them instead.
   */
  refTypeOptions?: Array<{ value: string; label: string }>;
  /** Shown when the rows can only be a time window around a subject, not the
   *  calls of one job. */
  approximate?: string;
};

const LlmAudit: React.FC<Props> = ({
  filters,
  title,
  refTypeOptions,
  approximate,
}) => {
  const [refType, setRefType] = useState<string | undefined>(
    refTypeOptions?.length ? refTypeOptions[0].value : filters.ref_type,
  );
  const [rows, setRows] = useState<LlmCallRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<LlmCallDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchLlmCalls({
        ...filters,
        ref_type: refType,
        status,
        page,
        page_size: PAGE_SIZE,
      });
      setRows(response.results);
      setCount(response.pagination.count);
    } catch (requestError) {
      setError(readableError(requestError, { what: 'These model calls' }));
      setRows([]);
    } finally {
      setLoading(false);
    }
    // filters is a literal from the parent render; its identity changes every
    // render, so the primitive members are the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refType, filters.ref_id, filters.job, status, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCall = async (row: LlmCallRow) => {
    setDetailLoading(true);
    try {
      setSelected(await fetchLlmCall(row.id));
    } catch (requestError) {
      setError(readableError(requestError, { what: 'That model call' }));
    } finally {
      setDetailLoading(false);
    }
  };

  const columns: any[] = [
    {
      title: 'When',
      dataIndex: 'created_at',
      width: 170,
      className: 'assessment-cell-nowrap',
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'Call',
      width: 200,
      render: (_: unknown, row: LlmCallRow) => (
        <Space direction="vertical" size={0}>
          <EllipsisText code value={displayCallType(row.call_type)} />
          {chips(row)}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (value: string) => (
        <Tag color={STATUS_COLOR[String(value).toLowerCase()] || 'default'}>
          {String(value).toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Answered by',
      dataIndex: 'model',
      width: 200,
      render: (value: string) => <EllipsisText code value={value} />,
    },
    {
      title: 'Attempt',
      width: 120,
      render: (_: unknown, row: LlmCallRow) => {
        const meta = row.metadata || {};
        const attempt = meta.attempt as number | undefined;
        const stage = meta.loosen_stage as string | undefined;
        if (attempt == null && !stage) return '—';
        return (
          <Space size={[4, 4]} wrap className="assessment-tag-group">
            {attempt != null && <Tag>try {attempt}</Tag>}
            {stage && <Tag>{stage}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Latency',
      dataIndex: 'latency_ms',
      width: 100,
      render: (value: number | null) => (value != null ? `${value} ms` : '—'),
    },
    {
      title: 'Tokens',
      width: 150,
      render: (_: unknown, row: LlmCallRow) => (
        <Space size={[4, 4]} wrap className="assessment-tag-group">
          <Tag>in {row.tokens_prompt ?? '?'}</Tag>
          <Tag>out {row.tokens_completion ?? '?'}</Tag>
          {row.tokens_reasoning ? (
            <Tag>think {row.tokens_reasoning}</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'First error',
      dataIndex: 'error',
      render: (value: string | null) =>
        value ? (
          <EllipsisText value={value} />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'right',
      width: 130,
      render: (_: unknown, row: LlmCallRow) => (
        <Button
          size="small"
          loading={detailLoading}
          onClick={() => openCall(row)}
        >
          Prompt and answer
        </Button>
      ),
    },
  ];

  return (
    <Card
      size="small"
      title={title || 'Model calls'}
      extra={
        <Space>
          {refTypeOptions && refTypeOptions.length > 1 && (
            <Select
              aria-label="Call subject"
              style={{ width: 220 }}
              value={refType}
              onChange={value => {
                setRefType(value);
                setPage(1);
              }}
              options={refTypeOptions}
            />
          )}
          <Select
            allowClear
            aria-label="Call status"
            placeholder="All statuses"
            style={{ width: 160 }}
            value={status}
            onChange={value => {
              setStatus(value);
              setPage(1);
            }}
            options={[
              { value: 'ok', label: 'Answered' },
              { value: 'error', label: 'Errored' },
            ]}
          />
          <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
            Refresh
          </Button>
        </Space>
      }
    >
      {approximate && (
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="These calls are matched by subject and time, not by job"
          description={approximate}
        />
      )}
      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}
      <Table
        rowKey="id"
        className="assessment-table"
        tableLayout="fixed"
        loading={loading}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 1400 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: count,
          showSizeChanger: false,
          onChange: setPage,
          showTotal: total => `${total} model call${total === 1 ? '' : 's'}`,
        }}
      />
      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        width={900}
        title={
          selected
            ? `Call #${selected.id} — ${displayCallType(selected.call_type)}`
            : ''
        }
        destroyOnHidden
      >
        <ScenarioErrorBoundary what="This call's prompt and answer">
          {selected && <CallDetail call={selected} />}
        </ScenarioErrorBoundary>
      </Drawer>
    </Card>
  );
};

export default LlmAudit;
